import Konva from 'konva';
import card_index, { cardName } from '../card_index';
import _flatten from 'lodash/flatten';
import { IRect } from 'konva/types/types';
import { ICard, ICardsRenderInfo, IGameState, IPile, requestState, CardState, SuperMove, ILaid } from '../redux/types';
import {
  CARD_DESKTOP_SIZE,
  CARD_PROPORTION,
  DESKTOP_OFFSET,
  OFFSET_BETWEEN_CARDS,
  SEPARATE_DESKTOP_SIZE,
} from '../constants';
import { DropListItem, IDropElement } from '../functions/hints';

export function delay(ms: number): Promise<any> {
  return new Promise(function (resolve) {
    setTimeout(resolve, ms);
  });
}

export function percent(value: number, context: number): number {
  return (context / 100) * value;
}

export function moveTo(node: any, x: number, y: number, duration: number = 0.1): Promise<any> {
  return new Promise<any>((resolve): void => {
    const tween = new Konva.Tween({
      node,
      x,
      y,
      duration,
      easing: Konva.Easings.Linear,
      onFinish: () => {
        resolve(1);
        tween.destroy();
      },
    });

    tween.play();
  });
}

export async function hintMove(node: any, x: number, y: number): Promise<any> {
  const rectX = node.x();
  const rectY = node.y();

  await delay(100);
  await moveTo(node, x, y, 0.3);
  await delay(500);
  await moveTo(node, rectX, rectY, 0.3);
  await delay(100);
}

export function getCardsRenderInfo(size: number): ICardsRenderInfo {
  const card_width: number = percent(CARD_DESKTOP_SIZE, size);
  const card_height: number = card_width * CARD_PROPORTION;

  return {
    width: card_width,
    height: card_height,
    corner: percent(6, card_width),
    normalShadow: percent(6, card_width),
    offset: percent(4, size),
  };
}

export function getGameX(size: number): number {
  const offset = percent(DESKTOP_OFFSET, size);
  return offset;
}

export function getPileX(pileIndex: number, size: number): number {
  const gameX: number = getGameX(size);
  const cardInfo: ICardsRenderInfo = getCardsRenderInfo(size);
  const ds: number = pileIndex > 3 ? percent(SEPARATE_DESKTOP_SIZE, size) : 0;
  const x: number = pileIndex * cardInfo.width + pileIndex * percent(OFFSET_BETWEEN_CARDS, size) + ds;
  return gameX + x;
}

export function getSlotY(size: number): number {
  return percent(DESKTOP_OFFSET, size);
}

export function getHomeX(index: number, size: number): number {
  const HOME_START_INDEX = 4;
  return getPileX(index + HOME_START_INDEX, size);
}

export function getPileY(size: number): number {
  const cardInfo: ICardsRenderInfo = getCardsRenderInfo(size);
  return percent(DESKTOP_OFFSET * 2, size) + cardInfo.height;
}

export function getCardYInPile(cardIndex: number, size: number): number {
  const cardInfo: ICardsRenderInfo = getCardsRenderInfo(size);
  return getPileY(size) + (cardIndex - 1) * cardInfo.offset;
}

export function haveIntersection(r1: IRect, r2: IRect): boolean {
  return !(r2.x > r1.x + r1.width || r2.x + r2.width < r1.x || r2.y > r1.y + r1.height || r2.y + r2.height < r1.y);
}

export function intersectionXDistance(r1: IRect, r2: IRect): number {
  return r1.x > r2.x ? r1.x + r1.width - r2.x : r2.x + r2.width - r1.x;
}

export function expandGameState(requestState: requestState): IGameState {
  const { laid } = requestState;
  const newLaid = laid.map(pile => {
    return pile.map(cardNumber => {
      return { ...card_index[cardNumber] };
    });
  });
  const gameState = { ...requestState, laid: newLaid };
  setCardCanDrag(gameState);
  setStatusForCards(gameState);
  return gameState;
}

export function makeGameStep(gameState: IGameState): void {
  setCardCanDrag(gameState);
  setStatusForCards(gameState);
}

export function isSingleCard(card: ICard): boolean {
  return Boolean(card.state === CardState.slots || (card.state === CardState.laid && card.isLastCard));
}

export function setStatusForCards(gameState: IGameState) {
  const { laid, slots, home } = gameState;
  laid.forEach((pile, pileIndex: any) => {
    pile.forEach((card: ICard, index) => {
      card.state = CardState.laid;
      card.index = pileIndex;
      card.pileSize = pile.length;
      card.isLastCard = index === pile.length - 1;
      card.isSingleCard = isSingleCard(card);
      card.cardIndex = index;
    });
  });

  slots.forEach((card, index: any) => {
    if (card) {
      card.state = CardState.slots;
      card.index = index;
      delete card.cardIndex;
    }
  });

  home.forEach((homeSlot, index: any) => {
    homeSlot.forEach(card => {
      card.state = CardState.home;
      card.index = index;
      delete card.cardIndex;
    });
  });
}

export function canDrop(cardOne: ICard, cardTwo: ICard | DropListItem): boolean {
  return cardOne.color !== cardTwo.color && cardOne.rate + 1 === cardTwo.rate;
}

export function canDropToHome(homeSlotCard: void | null | ICard, card: ICard): boolean {
  if (!homeSlotCard) return card.value === 'ace';
  return homeSlotCard.suit === card.suit && homeSlotCard.rate + 1 === card.rate;
}

export function setDragCardInPile(pile: IPile) {
  if (pile.length === 0) return;

  pile.forEach(card => {
    card.draggable = false;
    card.puttedWrong = true;
  });

  pile[pile.length - 1].draggable = true;
  if (pile.length === 1) return;

  for (let i = pile.length - 2; i >= 0; i--) {
    const card = pile[i];
    const prevCard = pile[i + 1];
    card.draggable = canDrop(prevCard, card);
    prevCard.puttedWrong = !card.draggable;
    if (!card.draggable) break;
  }
}

export function getCardsNames(cards: IPile): cardName[] {
  return cards.map(card => card.name);
}

export function setCardCanDrag(gameState: IGameState): void {
  const { laid, slots, home } = gameState;
  laid.forEach(setDragCardInPile);
  home.forEach(homeSlot => {
    homeSlot.forEach(card => {
      card.draggable = false;
    });
  });
  slots.forEach(card => {
    if (card) card.draggable = true;
  });
}

export function getCardsFormState(gameState: IGameState): IPile {
  const { laid, slots, home } = gameState;
  const result: IPile = [];

  laid.forEach(pile => {
    pile.forEach((card: ICard) => {
      result.push(card);
    });
  });

  home.forEach(homeSlot => {
    homeSlot.forEach((card: ICard) => {
      result.push(card);
    });
  });

  slots.forEach(card => {
    if (card) result.push(card);
  });

  return result;
}

export function getSuperMove(dropElements: IDropElement[], state: IGameState): SuperMove | false {
  if (!dropElements.length) return false;

  const homeDropElements = dropElements.filter(dropElement => dropElement.state === CardState.home);
  if (!homeDropElements.length) return false;

  const { home } = state;
  const homeCards = _flatten(home);
  let superElementIndex: number = 0;

  const dropElement = homeDropElements.find(_dropElement => {
    return Boolean(
      _dropElement.dropList.find((dropItem, dropItemIndex) => {
        const noNeedThisCard =
          dropItem.rate <= 2 ||
          homeCards.filter(card => card.rate + 2 === dropItem.rate && card.color !== dropItem.color).length === 2;
        if (noNeedThisCard) superElementIndex = dropItemIndex;
        return noNeedThisCard;
      })
    );
  });

  if (!dropElement) return false;
  const superElement = dropElement.dropList[superElementIndex];

  return { ...superElement, homeSlot: dropElement.index };
}

export function getDropList(cards: IPile): DropListItem[] {
  return cards.map(card => ({
    name: card.name,
    state: card.state,
    index: card.index,
    cardIndex: card.cardIndex,
    rate: card.rate,
    color: card.color,
    putWrong: Boolean(card.puttedWrong),
    pileSize: card.pileSize,
  }));
}

export function getGameSize(): number {
  if (document.querySelector('#content') !== null) {
    return (document.querySelector('#content') as HTMLElement).clientWidth;
  }

  return document.documentElement.clientWidth;
}

export function getNumberOfEmptySlots(slots: (ICard | null)[]): number {
  return slots.filter(slot => slot === null).length;
}

export function getNumberOfEmptyPiles(laid: ILaid): number {
  return laid.filter(pile => pile.length === 0).length;
}

export function getNumberOfMaxCardsCanBeDropped(emptySlots: number, emptyPiles: number, isDropToPile: boolean): number {
  return (emptySlots + 1) * Math.pow(2, isDropToPile ? emptyPiles - 1 : emptyPiles);
}

export function declOfNum(number: number, titles: string[]): string {
  const cases = [2, 0, 1, 1, 1, 2];
  return titles[number % 100 > 4 && number % 100 < 20 ? 2 : cases[number % 10 < 5 ? number % 10 : 5]];
}
